import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import errorFallbackImage from "@/assets/images/error-fallback.png";

const COUNTDOWN_SECONDS = 3;

export type ChunkErrorViewProps = { onReload?: () => void };

export const ChunkErrorView = ({ onReload }: ChunkErrorViewProps) => {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const handleReload = useCallback(() => {
    if (onReload) {
      onReload();
      return;
    }
    window.location.reload();
  }, [onReload]);

  useEffect(() => {
    if (countdown <= 0) {
      handleReload();
      return;
    }

    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, handleReload]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-lightgray p-4 dark:bg-dark">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex justify-center">
          <img
            src={errorFallbackImage}
            alt="Mise à jour disponible"
            className="h-56 w-auto object-contain"
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-primary/10 shadow-xl dark:border-darkborder dark:bg-primary/5">
          <div className="flex flex-col items-center p-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/10">
              <RefreshCw className="h-8 w-8 text-primary animate-spin" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-dark dark:text-white">
              Nouvelle version disponible
            </h1>
            <p className="text-base text-muted dark:text-lightmuted">
              L'application a été mise à jour. La page va se recharger automatiquement...
            </p>
            <p className="mt-4 text-2xl font-semibold text-primary">
              Rechargement dans
              {" "}
              {countdown}
              ...
            </p>
            <button
              type="button"
              onClick={handleReload}
              className="mt-6 w-full rounded-lg bg-primary px-6 py-4 text-lg font-semibold text-white shadow-sm transition-colors hover:bg-primary-emphasis focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-dark"
            >
              Recharger maintenant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ChunkErrorView.displayName = "ChunkErrorView";
